import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { TAKE_BACK_BAG_URL } from '../../../config/externalURL';
import { ROUTE_MEMBERSHIP } from '../../../config/routes';
import { getGiftCardBrandDenominationOptions, getGiftCardDenominationsMessage, getGiftCardRatio } from '../../../utils/giftCards';
import { BUTTON_SIZE, BUTTON_TYPE, Button } from '../../../global/Button';
import TermsDisclaimer from '../../../global/TermsDisclaimer/TermsDisclaimer';
import GiftCardContentSummary from './GiftCardContentSummary';
import SliderSelector from '../../Reward/SliderSelector';
import GiftCardCover from './GiftCardCover';
import TCCoin from '../../../images/trashie-cash-v3.svg';

import './GiftCardContent.scss';
import { formatReadableNumber } from '../../../utils/numbers';

const GiftCardContentMain = ({
  giftCardBrandItem,
  selectedValue,
  onSelect,
  onSubmit,
  onShowTerms,
  currentBalance,
  disabled,
  submitDisabled,
  minBalance,
  minDenomination,
  trackerProps = {},
  hasActiveSubscription,
}) => {
  const history = useHistory();

  const {
    ratio,
    options,
    message,
  } = useMemo(() => ({
    ratio: getGiftCardRatio(giftCardBrandItem.tcRatio),
    options: getGiftCardBrandDenominationOptions(giftCardBrandItem.denominations),
    message: getGiftCardDenominationsMessage(giftCardBrandItem.denominations),
  }), [giftCardBrandItem]);

  const handleSubmit = useCallback(() => {
    if (submitDisabled) {
      if (hasActiveSubscription) {
        history.push(ROUTE_MEMBERSHIP);
        return;
      }
      window.location.href = TAKE_BACK_BAG_URL;
      return;
    }

    onSubmit?.();
  }, [onSubmit, submitDisabled, hasActiveSubscription, history]);

  const handleEarnMoreClick = useCallback((e) => {
    e.preventDefault();
    if (hasActiveSubscription) {
      history.push(ROUTE_MEMBERSHIP);
    } else {
      window.location.href = TAKE_BACK_BAG_URL;
    }
  }, [hasActiveSubscription, history]);

  const CTAButtonLabel = useMemo(() => {
    if (submitDisabled) {
      return hasActiveSubscription ? 'ORDER A TAKE BACK BAG' : 'BUY A TAKE BACK BAG';
    }
    return 'GET THIS GIFT CARD';
  }, [submitDisabled, hasActiveSubscription]);

  return (
    <div className="GiftCardContentMain">
      <GiftCardCover logoUrl={giftCardBrandItem.giftCardUrl} />
      <div className="GiftCardContentMain__content">
        <div className="GiftCardContentMain__content-action">
          {disabled ? (
            <div className="GiftCardContentMain__content-action-message">
              <p className="GiftCardContentMain__content-action-message-title">
                <button type="button" onClick={handleEarnMoreClick} className="GiftCardContentMain__content-action-message-title-button">
                  EARN MORE
                </button>
                {' '}
                TRASHIECASH™ TO UNLOCK GIFT CARD
              </p>
              <p className="GiftCardContentMain__content-action-message-desc">
                <span>You need at least </span>
                <img src={TCCoin} alt="TCH" />
                <span>{` ${formatReadableNumber(minBalance)} to get a $${minDenomination} ${giftCardBrandItem.name} Gift Card.`}</span>
              </p>
            </div>
          ) : (
            <SliderSelector
              value={selectedValue}
              title="GIFT CARD AMOUNT"
              message={message}
              options={options}
              onChange={onSelect}
              currentBalance={currentBalance}
              ratio={ratio}
            />
          )}
          <Button
            className="GiftCardContentMain__content-action-button"
            type={submitDisabled ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            onClick={handleSubmit}
            trackerProps={submitDisabled ? {} : trackerProps}
          >
            {CTAButtonLabel}
          </Button>
        </div>
        <GiftCardContentSummary title="GIFT CARD DETAILS" />
        <TermsDisclaimer
          terms={giftCardBrandItem.termsCopy}
          ctaText="VIEW ALL TERMS AND INSTRUCTIONS"
          termsTitle="TERMS AND INSTRUCTIONS"
          onHandleClick={onShowTerms}
        />
      </div>
    </div>
  );
};

export default GiftCardContentMain;
